import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '../components/ui/dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import {
    Tabs,
    TabsContent,
    TabsList,
    TabsTrigger,
} from '../components/ui/tabs';
import { Spinner, X, Upload, Image as ImageIcon, Plus, Bank, GlobeHemisphereEast } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { Checkbox } from '../components/ui/checkbox';

const API = process.env.REACT_APP_BACKEND_URL;

const PROPERTY_TYPE_OPTIONS = [
    { value: 'apartment', label: 'Wohnung' },
    { value: 'house', label: 'Haus' },
    { value: 'land', label: 'Grundstück' },
    { value: 'office', label: 'Büro' },
    { value: 'retail', label: 'Einzelhandel' },
    { value: 'warehouse', label: 'Lager/Halle' },
    { value: 'other', label: 'Sonstige' },
];

const MARKETING_TYPE_OPTIONS = [
    { value: 'sale', label: 'Kauf' },
    { value: 'rent', label: 'Miete' },
];

const STATUS_OPTIONS = [
    { value: 'active', label: 'Aktiv' },
    { value: 'inactive', label: 'Inaktiv' },
    { value: 'sold', label: 'Verkauft' },
    { value: 'rented', label: 'Vermietet' },
];

const CONDITION_OPTIONS = [
    { value: 'ERSTBEZUG', label: 'Erstbezug' },
    { value: 'NEUWERTIG', label: 'Neuwertig' },
    { value: 'MODERNISIERT', label: 'Modernisiert' },
    { value: 'VOLLSTAENDIG_RENOVIERT', label: 'Vollständig renoviert' },
    { value: 'TEIL_VOLLRENOVIERUNGSBED', label: 'Renovierungsbedürftig' },
    { value: 'SANIERT', label: 'Saniert' },
    { value: 'GEPFLEGT', label: 'Gepflegt' },
    { value: 'ABBRUCHREIF', label: 'Abbruchreif' },
];

const ENERGY_CERT_TYPE_OPTIONS = [
    { value: 'VERBRAUCH', label: 'Verbrauchsausweis' },
    { value: 'BEDARF', label: 'Bedarfsausweis' },
];

const ENERGY_CLASS_OPTIONS = [
    { value: 'A+', label: 'A+' },
    { value: 'A', label: 'A' },
    { value: 'B', label: 'B' },
    { value: 'C', label: 'C' },
    { value: 'D', label: 'D' },
    { value: 'E', label: 'E' },
    { value: 'F', label: 'F' },
    { value: 'G', label: 'G' },
    { value: 'H', label: 'H' },
];

const HEATING_TYPE_OPTIONS = [
    { value: 'zentralheizung', label: 'Zentralheizung' },
    { value: 'etagenheizung', label: 'Etagenheizung' },
    { value: 'fussbodenheizung', label: 'Fußbodenheizung' },
    { value: 'fernwaerme', label: 'Fernwärme' },
    { value: 'gas', label: 'Gas' },
    { value: 'oel', label: 'Öl' },
    { value: 'waermepumpe', label: 'Wärmepumpe' },
    { value: 'solar', label: 'Solar' },
    { value: 'pellet', label: 'Pellet' },
];

const ENERGY_CARRIER_OPTIONS = [
    { value: 'gas', label: 'Gas' },
    { value: 'oel', label: 'Öl' },
    { value: 'fernwaerme', label: 'Fernwärme' },
    { value: 'strom', label: 'Strom' },
    { value: 'pellet', label: 'Pellet' },
    { value: 'holz', label: 'Holz' },
    { value: 'solar', label: 'Solar' },
    { value: 'erdwaerme', label: 'Erdwärme' },
    { value: 'kohle', label: 'Kohle' },
    { value: 'fluessiggas', label: 'Flüssiggas' },
    { value: 'nahwaerme', label: 'Nahwärme' },
    { value: 'waermepumpe', label: 'Wärmepumpe' },
    { value: 'wind', label: 'Wind' },
    { value: 'wasser', label: 'Wasser' },
];

export function PropertyEditDialog({ property, open, onOpenChange, onSaved }) {
    const fileInputRef = useRef(null);
    const [formData, setFormData] = useState({
        title: '',
        description: '',
        location_description: '',
        property_type: 'apartment',
        marketing_type: 'sale',
        price: 0,
        currency: 'EUR',
        status: 'active',
        // Flächen
        area: '',
        plot_area: '',
        usable_area: '',
        total_area: '',
        rentable_area: '',
        // Zimmer
        rooms: '',
        bedrooms: '',
        bathrooms: '',
        // Zustand
        year_built: '',
        condition: 'none',
        last_renovation: '',
        floor: '',
        floors_total: '',
        // Energieausweis
        energy_certificate_type: 'none',
        energy_efficiency_class: 'none',
        energy_value: '',
        energy_certificate_valid_until: '',
        heating_type: 'none',
        energy_carrier: 'none',
        energy_with_hot_water: 'none',
        energy_electricity_value: '',
        energy_heat_value: '',
        energy_created_date: '',
        energy_year_standard: '',
        energy_building_type: '',
        energy_end_demand: '',
        // Preise
        commission: '',
        commission_note: '',
        price_per_sqm: '',
        rental_income: '',
        additional_costs: '',
        // Adresse
        street: '',
        house_number: '',
        zip: '',
        city: '',
        country: 'DE',
        // Sonstiges
        features: '',
        is_investment: false,
        is_international: false
    });
    const [brokers, setBrokers] = useState([]);
    const [brokerId, setBrokerId] = useState('none');
    const [submitting, setSubmitting] = useState(false);
    const [images, setImages] = useState([]);
    const [uploadingImages, setUploadingImages] = useState(false);

    useEffect(() => {
        if (property) {
            setFormData({
                title: property.title || '',
                description: property.description || '',
                location_description: property.location_description || '',
                property_type: property.property_type || 'apartment',
                marketing_type: property.marketing_type || 'sale',
                price: property.price || 0,
                currency: property.currency || 'EUR',
                status: property.status || 'active',
                // Flächen
                area: property.area || '',
                plot_area: property.plot_area || '',
                usable_area: property.usable_area || '',
                total_area: property.total_area || '',
                rentable_area: property.rentable_area || '',
                // Zimmer
                rooms: property.rooms || '',
                bedrooms: property.bedrooms || '',
                bathrooms: property.bathrooms || '',
                // Zustand
                year_built: property.year_built || '',
                condition: property.condition || 'none',
                last_renovation: property.last_renovation || '',
                floor: property.floor || '',
                floors_total: property.floors_total || '',
                // Energieausweis
                energy_certificate_type: property.energy_certificate_type || 'none',
                energy_efficiency_class: property.energy_efficiency_class || 'none',
                energy_value: property.energy_value || '',
                energy_certificate_valid_until: property.energy_certificate_valid_until || '',
                heating_type: property.heating_type || 'none',
                energy_carrier: property.energy_carrier || 'none',
                energy_with_hot_water: property.energy_with_hot_water === true ? 'yes' : property.energy_with_hot_water === false ? 'no' : 'none',
                energy_electricity_value: property.energy_electricity_value || '',
                energy_heat_value: property.energy_heat_value || '',
                energy_created_date: property.energy_created_date || '',
                energy_year_standard: property.energy_year_standard || '',
                energy_building_type: property.energy_building_type || '',
                energy_end_demand: property.energy_end_demand || '',
                // Preise
                commission: property.commission || '',
                commission_note: property.commission_note || '',
                price_per_sqm: property.price_per_sqm || '',
                rental_income: property.rental_income || '',
                additional_costs: property.additional_costs || '',
                // Adresse
                street: property.address?.street || '',
                house_number: property.address?.house_number || '',
                zip: property.address?.zip || '',
                city: property.address?.city || '',
                country: property.address?.country || 'DE',
                // Sonstiges
                features: (property.features || []).join('\n'),
                is_investment: property.is_investment === true,
                is_international: property.is_international === true
            });
            setBrokerId(property.broker_id || 'none');
            setImages(property.images || []);
        } else {
            // Reset for new property
            setImages([]);
        }
    }, [property]);

    useEffect(() => {
        const fetchBrokers = async () => {
            try {
                const { data } = await axios.get(`${API}/api/brokers`, { withCredentials: true });
                setBrokers(data);
            } catch (error) {
                console.error('Error fetching brokers:', error);
            }
        };
        if (open) {
            fetchBrokers();
        }
    }, [open]);

    const handleImageUpload = async (e) => {
        const files = e.target.files;
        if (!files || files.length === 0 || !property?.id) return;

        setUploadingImages(true);
        const formData = new FormData();
        for (let i = 0; i < files.length; i++) {
            formData.append('files', files[i]);
        }

        try {
            const { data } = await axios.post(
                `${API}/api/properties/${property.id}/images`,
                formData,
                { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } }
            );
            setImages(data.images);
            toast.success(`${data.uploaded} Bild(er) hochgeladen`);
        } catch (error) {
            toast.error('Fehler beim Hochladen');
        }
        setUploadingImages(false);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const handleDeleteImage = async (index) => {
        if (!property?.id) return;

        try {
            const { data } = await axios.delete(
                `${API}/api/properties/${property.id}/images/${index}`,
                { withCredentials: true }
            );
            setImages(data.images);
            toast.success('Bild gelöscht');
        } catch (error) {
            toast.error('Fehler beim Löschen');
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);

        const payload = {
            title: formData.title,
            description: formData.description || null,
            location_description: formData.location_description || null,
            property_type: formData.property_type,
            marketing_type: formData.marketing_type,
            price: parseFloat(formData.price) || 0,
            currency: formData.currency,
            status: formData.status,
            // Flächen
            area: formData.area ? parseFloat(formData.area) : null,
            plot_area: formData.plot_area ? parseFloat(formData.plot_area) : null,
            usable_area: formData.usable_area ? parseFloat(formData.usable_area) : null,
            total_area: formData.total_area ? parseFloat(formData.total_area) : null,
            rentable_area: formData.rentable_area ? parseFloat(formData.rentable_area) : null,
            // Zimmer
            rooms: formData.rooms ? parseFloat(formData.rooms) : null,
            bedrooms: formData.bedrooms ? parseInt(formData.bedrooms) : null,
            bathrooms: formData.bathrooms ? parseInt(formData.bathrooms) : null,
            // Zustand
            year_built: formData.year_built ? parseInt(formData.year_built) : null,
            condition: formData.condition !== 'none' ? formData.condition : null,
            last_renovation: formData.last_renovation ? parseInt(formData.last_renovation) : null,
            floor: formData.floor ? parseInt(formData.floor) : null,
            floors_total: formData.floors_total ? parseInt(formData.floors_total) : null,
            // Energieausweis
            energy_certificate_type: formData.energy_certificate_type !== 'none' ? formData.energy_certificate_type : null,
            energy_efficiency_class: formData.energy_efficiency_class !== 'none' ? formData.energy_efficiency_class : null,
            energy_value: formData.energy_value ? parseFloat(formData.energy_value) : null,
            energy_certificate_valid_until: formData.energy_certificate_valid_until || null,
            heating_type: formData.heating_type !== 'none' ? formData.heating_type : null,
            energy_carrier: formData.energy_carrier !== 'none' ? formData.energy_carrier : null,
            energy_with_hot_water: formData.energy_with_hot_water === 'yes' ? true : formData.energy_with_hot_water === 'no' ? false : null,
            energy_electricity_value: formData.energy_electricity_value ? parseFloat(formData.energy_electricity_value) : null,
            energy_heat_value: formData.energy_heat_value ? parseFloat(formData.energy_heat_value) : null,
            energy_created_date: formData.energy_created_date || null,
            energy_year_standard: formData.energy_year_standard || null,
            energy_building_type: formData.energy_building_type || null,
            energy_end_demand: formData.energy_end_demand ? parseFloat(formData.energy_end_demand) : null,
            // Preise
            commission: formData.commission || null,
            commission_note: formData.commission_note || null,
            price_per_sqm: formData.price_per_sqm ? parseFloat(formData.price_per_sqm) : null,
            rental_income: formData.rental_income ? parseFloat(formData.rental_income) : null,
            additional_costs: formData.additional_costs ? parseFloat(formData.additional_costs) : null,
            // Sonstiges
            broker_id: brokerId === 'none' ? null : brokerId,
            address: {
                street: formData.street || null,
                house_number: formData.house_number || null,
                zip: formData.zip || null,
                city: formData.city || null,
                country: formData.country || 'DE'
            },
            features: formData.features.split('\n').filter(f => f.trim()),
            is_investment: formData.is_investment === true,
            is_international: formData.is_international === true
        };

        try {
            if (property?.id) {
                await axios.put(`${API}/api/properties/${property.id}`, payload, { withCredentials: true });
                toast.success('Immobilie aktualisiert');
            } else {
                const { data } = await axios.post(`${API}/api/properties`, payload, { withCredentials: true });
                // If we have images to upload for new property, do it now
                toast.success('Immobilie erstellt');
            }
            onSaved?.();
            onOpenChange(false);
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Fehler beim Speichern');
        }
        setSubmitting(false);
    };

    const getImageUrl = (img) => {
        if (img.startsWith('http')) return img;
        return `${API}/api/public/files/${img}`;
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[900px] max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle style={{ fontFamily: 'var(--font-cabinet)' }}>
                        {property?.id ? 'Immobilie bearbeiten' : 'Neue Immobilie'}
                    </DialogTitle>
                    <DialogDescription>
                        Füllen Sie die Immobiliendaten aus
                    </DialogDescription>
                </DialogHeader>
                <form onSubmit={handleSubmit}>
                    <Tabs defaultValue="basic" className="w-full">
                        <TabsList className="grid grid-cols-5 mb-4">
                            <TabsTrigger value="basic">Basis</TabsTrigger>
                            <TabsTrigger value="areas">Flächen</TabsTrigger>
                            <TabsTrigger value="condition">Zustand</TabsTrigger>
                            <TabsTrigger value="prices">Preise</TabsTrigger>
                            <TabsTrigger value="images">Bilder</TabsTrigger>
                        </TabsList>

                        {/* Basic Tab */}
                        <TabsContent value="basic" className="space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div className="col-span-2 space-y-2">
                                    <Label htmlFor="title">Titel *</Label>
                                    <Input
                                        id="title"
                                        value={formData.title}
                                        onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                                        required
                                        className="rounded-sm"
                                        data-testid="edit-title-input"
                                    />
                                </div>
                                
                                <div className="space-y-2">
                                    <Label>Immobilientyp</Label>
                                    <Select value={formData.property_type} onValueChange={(v) => setFormData({ ...formData, property_type: v })}>
                                        <SelectTrigger className="rounded-sm"><SelectValue /></SelectTrigger>
                                        <SelectContent>
                                            {PROPERTY_TYPE_OPTIONS.map((opt) => (
                                                <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                                
                                <div className="space-y-2">
                                    <Label>Vermarktung</Label>
                                    <Select value={formData.marketing_type} onValueChange={(v) => setFormData({ ...formData, marketing_type: v })}>
                                        <SelectTrigger className="rounded-sm"><SelectValue /></SelectTrigger>
                                        <SelectContent>
                                            {MARKETING_TYPE_OPTIONS.map((opt) => (
                                                <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                                
                                <div className="space-y-2">
                                    <Label>Status</Label>
                                    <Select value={formData.status} onValueChange={(v) => setFormData({ ...formData, status: v })}>
                                        <SelectTrigger className="rounded-sm"><SelectValue /></SelectTrigger>
                                        <SelectContent>
                                            {STATUS_OPTIONS.map((opt) => (
                                                <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                                
                                <div className="space-y-2">
                                    <Label>Makler</Label>
                                    <Select value={brokerId} onValueChange={setBrokerId}>
                                        <SelectTrigger className="rounded-sm"><SelectValue placeholder="Kein Makler" /></SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="none">Kein Makler</SelectItem>
                                            {brokers.map((broker) => (
                                                <SelectItem key={broker.id} value={broker.id}>{broker.name}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>

                            {/* Anlageimmobilie Marker */}
                            <div className="border rounded-sm p-3 bg-amber-50/50 border-amber-200">
                                <div className="flex items-start gap-3">
                                    <Checkbox
                                        id="is-investment"
                                        checked={formData.is_investment === true}
                                        onCheckedChange={(checked) => setFormData({ ...formData, is_investment: checked === true })}
                                        data-testid="edit-is-investment-checkbox"
                                        className="mt-0.5"
                                    />
                                    <div className="space-y-1 flex-1">
                                        <Label
                                            htmlFor="is-investment"
                                            className="flex items-center gap-2 cursor-pointer text-sm font-semibold"
                                        >
                                            <Bank size={16} weight="fill" className="text-amber-600" />
                                            Anlageimmobilie
                                        </Label>
                                        <p className="text-xs text-muted-foreground leading-snug">
                                            Mit Häkchen wird die Immobilie ausschließlich im Embed-Code „Anlageimmobilien“ angezeigt und aus allen anderen Embeds sowie der öffentlichen Suche ausgeblendet.
                                        </p>
                                    </div>
                                </div>
                            </div>

                            {/* Auslandsimmobilie Marker */}
                            <div className="border rounded-sm p-3 bg-sky-50/50 border-sky-200">
                                <div className="flex items-start gap-3">
                                    <Checkbox
                                        id="is-international"
                                        checked={formData.is_international === true}
                                        onCheckedChange={(checked) => setFormData({ ...formData, is_international: checked === true })}
                                        data-testid="edit-is-international-checkbox"
                                        className="mt-0.5"
                                    />
                                    <div className="space-y-1 flex-1">
                                        <Label
                                            htmlFor="is-international"
                                            className="flex items-center gap-2 cursor-pointer text-sm font-semibold"
                                        >
                                            <GlobeHemisphereEast size={16} weight="fill" className="text-sky-600" />
                                            Auslandsimmobilie
                                        </Label>
                                        <p className="text-xs text-muted-foreground leading-snug">
                                            Mit Häkchen wird die Immobilie ausschließlich im Embed-Code „Immobilien International“ angezeigt – nützlich, wenn das Estate Office fälschlicherweise „Deutschland“ pflegt. Objekte mit Land ≠ Deutschland gelten automatisch als Auslandsimmobilie, auch ohne Häkchen.
                                        </p>
                                    </div>
                                </div>
                            </div>

                            {/* Address */}
                            <div className="space-y-2 pt-2 border-t">
                                <Label className="text-base font-semibold">Adresse</Label>
                                <div className="grid grid-cols-4 gap-4">
                                    <div className="col-span-2 space-y-2">
                                        <Label htmlFor="street">Straße</Label>
                                        <Input id="street" value={formData.street} onChange={(e) => setFormData({ ...formData, street: e.target.value })} className="rounded-sm" />
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="house_number">Hausnr.</Label>
                                        <Input id="house_number" value={formData.house_number} onChange={(e) => setFormData({ ...formData, house_number: e.target.value })} className="rounded-sm" />
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="zip">PLZ</Label>
                                        <Input id="zip" value={formData.zip} onChange={(e) => setFormData({ ...formData, zip: e.target.value })} className="rounded-sm" />
                                    </div>
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label htmlFor="city">Stadt</Label>
                                        <Input id="city" value={formData.city} onChange={(e) => setFormData({ ...formData, city: e.target.value })} className="rounded-sm" />
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="country">Land</Label>
                                        <Input id="country" value={formData.country} onChange={(e) => setFormData({ ...formData, country: e.target.value })} className="rounded-sm" />
                                    </div>
                                </div>
                            </div>

                            {/* Description */}
                            <div className="space-y-2">
                                <Label htmlFor="description">Beschreibung</Label>
                                <Textarea id="description" value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} rows={4} className="rounded-sm resize-none" />
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="location_description">Lagebeschreibung</Label>
                                <Textarea id="location_description" value={formData.location_description} onChange={(e) => setFormData({ ...formData, location_description: e.target.value })} rows={3} className="rounded-sm resize-none" />
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="features">Ausstattung (eine pro Zeile)</Label>
                                <Textarea id="features" value={formData.features} onChange={(e) => setFormData({ ...formData, features: e.target.value })} rows={3} placeholder="Balkon&#10;Einbauküche&#10;Fußbodenheizung" className="rounded-sm resize-none" />
                            </div>
                        </TabsContent>

                        {/* Areas Tab */}
                        <TabsContent value="areas" className="space-y-4">
                            <div className="grid grid-cols-3 gap-4">
                                <div className="space-y-2">
                                    <Label>Wohnfläche (m²)</Label>
                                    <Input type="number" min="0" step="0.1" value={formData.area} onChange={(e) => setFormData({ ...formData, area: e.target.value })} className="rounded-sm" />
                                </div>
                                <div className="space-y-2">
                                    <Label>Grundstücksfläche (m²)</Label>
                                    <Input type="number" min="0" step="0.1" value={formData.plot_area} onChange={(e) => setFormData({ ...formData, plot_area: e.target.value })} className="rounded-sm" />
                                </div>
                                <div className="space-y-2">
                                    <Label>Nutzfläche (m²)</Label>
                                    <Input type="number" min="0" step="0.1" value={formData.usable_area} onChange={(e) => setFormData({ ...formData, usable_area: e.target.value })} className="rounded-sm" />
                                </div>
                                <div className="space-y-2">
                                    <Label>Gesamtfläche (m²)</Label>
                                    <Input type="number" min="0" step="0.1" value={formData.total_area} onChange={(e) => setFormData({ ...formData, total_area: e.target.value })} className="rounded-sm" />
                                </div>
                                <div className="space-y-2">
                                    <Label>Vermietbare Fläche (m²)</Label>
                                    <Input type="number" min="0" step="0.1" value={formData.rentable_area} onChange={(e) => setFormData({ ...formData, rentable_area: e.target.value })} className="rounded-sm" />
                                </div>
                            </div>

                            <div className="grid grid-cols-4 gap-4 pt-4 border-t">
                                <div className="space-y-2">
                                    <Label>Zimmer</Label>
                                    <Input type="number" min="0" step="0.5" value={formData.rooms} onChange={(e) => setFormData({ ...formData, rooms: e.target.value })} className="rounded-sm" />
                                </div>
                                <div className="space-y-2">
                                    <Label>Schlafzimmer</Label>
                                    <Input type="number" min="0" value={formData.bedrooms} onChange={(e) => setFormData({ ...formData, bedrooms: e.target.value })} className="rounded-sm" />
                                </div>
                                <div className="space-y-2">
                                    <Label>Badezimmer</Label>
                                    <Input type="number" min="0" value={formData.bathrooms} onChange={(e) => setFormData({ ...formData, bathrooms: e.target.value })} className="rounded-sm" />
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4 pt-4 border-t">
                                <div className="space-y-2">
                                    <Label>Etage</Label>
                                    <Input type="number" min="0" value={formData.floor} onChange={(e) => setFormData({ ...formData, floor: e.target.value })} className="rounded-sm" />
                                </div>
                                <div className="space-y-2">
                                    <Label>Gesamtetagen</Label>
                                    <Input type="number" min="0" value={formData.floors_total} onChange={(e) => setFormData({ ...formData, floors_total: e.target.value })} className="rounded-sm" />
                                </div>
                            </div>
                        </TabsContent>

                        {/* Condition Tab */}
                        <TabsContent value="condition" className="space-y-4">
                            <div className="grid grid-cols-3 gap-4">
                                <div className="space-y-2">
                                    <Label>Baujahr</Label>
                                    <Input type="number" min="1800" max="2030" value={formData.year_built} onChange={(e) => setFormData({ ...formData, year_built: e.target.value })} className="rounded-sm" placeholder="z.B. 1990" />
                                </div>
                                <div className="space-y-2">
                                    <Label>Zustand</Label>
                                    <Select value={formData.condition} onValueChange={(v) => setFormData({ ...formData, condition: v })}>
                                        <SelectTrigger className="rounded-sm"><SelectValue placeholder="Nicht angegeben" /></SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="none">Nicht angegeben</SelectItem>
                                            {CONDITION_OPTIONS.map((opt) => (
                                                <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="space-y-2">
                                    <Label>Letzte Renovierung</Label>
                                    <Input type="number" min="1900" max="2030" value={formData.last_renovation} onChange={(e) => setFormData({ ...formData, last_renovation: e.target.value })} className="rounded-sm" placeholder="z.B. 2020" />
                                </div>
                            </div>

                            <div className="pt-4 border-t space-y-4">
                                <Label className="text-base font-semibold">Energieausweis</Label>
                                <div className="grid grid-cols-3 gap-4">
                                    <div className="space-y-2">
                                        <Label>Art</Label>
                                        <Select value={formData.energy_certificate_type} onValueChange={(v) => setFormData({ ...formData, energy_certificate_type: v })}>
                                            <SelectTrigger className="rounded-sm"><SelectValue placeholder="Nicht vorhanden" /></SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="none">Nicht vorhanden</SelectItem>
                                                {ENERGY_CERT_TYPE_OPTIONS.map((opt) => (
                                                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Effizienzklasse</Label>
                                        <Select value={formData.energy_efficiency_class} onValueChange={(v) => setFormData({ ...formData, energy_efficiency_class: v })}>
                                            <SelectTrigger className="rounded-sm"><SelectValue placeholder="Nicht angegeben" /></SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="none">Nicht angegeben</SelectItem>
                                                {ENERGY_CLASS_OPTIONS.map((opt) => (
                                                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Kennwert (kWh/m²a)</Label>
                                        <Input type="number" min="0" step="0.1" value={formData.energy_value} onChange={(e) => setFormData({ ...formData, energy_value: e.target.value })} className="rounded-sm" data-testid="edit-energy-value" />
                                    </div>
                                </div>
                                <div className="grid grid-cols-3 gap-4">
                                    <div className="space-y-2">
                                        <Label>Endenergiebedarf (kWh/m²a)</Label>
                                        <Input type="number" min="0" step="0.1" value={formData.energy_end_demand} onChange={(e) => setFormData({ ...formData, energy_end_demand: e.target.value })} className="rounded-sm" data-testid="edit-energy-end-demand" />
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Wesentlicher Energieträger</Label>
                                        <Select value={formData.energy_carrier} onValueChange={(v) => setFormData({ ...formData, energy_carrier: v })}>
                                            <SelectTrigger className="rounded-sm"><SelectValue placeholder="Nicht angegeben" /></SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="none">Nicht angegeben</SelectItem>
                                                {ENERGY_CARRIER_OPTIONS.map((opt) => (
                                                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Warmwasser enthalten</Label>
                                        <Select value={formData.energy_with_hot_water} onValueChange={(v) => setFormData({ ...formData, energy_with_hot_water: v })}>
                                            <SelectTrigger className="rounded-sm"><SelectValue placeholder="Nicht angegeben" /></SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="none">Nicht angegeben</SelectItem>
                                                <SelectItem value="yes">Ja</SelectItem>
                                                <SelectItem value="no">Nein</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                </div>
                                <div className="grid grid-cols-3 gap-4">
                                    <div className="space-y-2">
                                        <Label>Gültig bis</Label>
                                        <Input value={formData.energy_certificate_valid_until} onChange={(e) => setFormData({ ...formData, energy_certificate_valid_until: e.target.value })} className="rounded-sm" placeholder="z.B. 30.01.2029" data-testid="edit-energy-valid" />
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Heizungsart</Label>
                                        <Select value={formData.heating_type} onValueChange={(v) => setFormData({ ...formData, heating_type: v })}>
                                            <SelectTrigger className="rounded-sm"><SelectValue placeholder="Nicht angegeben" /></SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="none">Nicht angegeben</SelectItem>
                                                {HEATING_TYPE_OPTIONS.map((opt) => (
                                                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Ausstellungsdatum</Label>
                                        <Input value={formData.energy_created_date} onChange={(e) => setFormData({ ...formData, energy_created_date: e.target.value })} className="rounded-sm" placeholder="z.B. nach 01.05.2014" data-testid="edit-energy-created" />
                                    </div>
                                </div>
                                <div className="grid grid-cols-3 gap-4">
                                    <div className="space-y-2">
                                        <Label>Stromwert (kWh/m²a)</Label>
                                        <Input type="number" min="0" step="0.1" value={formData.energy_electricity_value} onChange={(e) => setFormData({ ...formData, energy_electricity_value: e.target.value })} className="rounded-sm" />
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Wärmewert (kWh/m²a)</Label>
                                        <Input type="number" min="0" step="0.1" value={formData.energy_heat_value} onChange={(e) => setFormData({ ...formData, energy_heat_value: e.target.value })} className="rounded-sm" />
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Jahrgang / Standard</Label>
                                        <Input value={formData.energy_year_standard} onChange={(e) => setFormData({ ...formData, energy_year_standard: e.target.value })} className="rounded-sm" placeholder="z.B. 2014" />
                                    </div>
                                </div>
                            </div>
                        </TabsContent>

                        {/* Prices Tab */}
                        <TabsContent value="prices" className="space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label>Preis (EUR) *</Label>
                                    <Input type="number" min="0" step="0.01" value={formData.price} onChange={(e) => setFormData({ ...formData, price: e.target.value })} required className="rounded-sm" />
                                </div>
                                <div className="space-y-2">
                                    <Label>Preis pro m²</Label>
                                    <Input type="number" min="0" step="0.01" value={formData.price_per_sqm} onChange={(e) => setFormData({ ...formData, price_per_sqm: e.target.value })} className="rounded-sm" />
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label>Provision</Label>
                                    <Input value={formData.commission} onChange={(e) => setFormData({ ...formData, commission: e.target.value })} className="rounded-sm" placeholder="z.B. 3,57% inkl. MwSt." />
                                </div>
                                <div className="space-y-2">
                                    <Label>Provisionshinweis</Label>
                                    <Input value={formData.commission_note} onChange={(e) => setFormData({ ...formData, commission_note: e.target.value })} className="rounded-sm" />
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4 pt-4 border-t">
                                <div className="space-y-2">
                                    <Label>Mieteinnahmen p.a. (EUR)</Label>
                                    <Input type="number" min="0" step="0.01" value={formData.rental_income} onChange={(e) => setFormData({ ...formData, rental_income: e.target.value })} className="rounded-sm" />
                                </div>
                                <div className="space-y-2">
                                    <Label>Nebenkosten (EUR)</Label>
                                    <Input type="number" min="0" step="0.01" value={formData.additional_costs} onChange={(e) => setFormData({ ...formData, additional_costs: e.target.value })} className="rounded-sm" />
                                </div>
                            </div>
                        </TabsContent>

                        {/* Images Tab */}
                        <TabsContent value="images" className="space-y-4">
                            {property?.id ? (
                                <>
                                    <div className="flex items-center justify-between">
                                        <Label className="text-base font-semibold">Bilder ({images.length})</Label>
                                        <div>
                                            <input
                                                ref={fileInputRef}
                                                type="file"
                                                accept="image/*"
                                                multiple
                                                onChange={handleImageUpload}
                                                className="hidden"
                                                id="image-upload"
                                            />
                                            <Button
                                                type="button"
                                                variant="outline"
                                                className="rounded-sm"
                                                onClick={() => fileInputRef.current?.click()}
                                                disabled={uploadingImages}
                                            >
                                                {uploadingImages ? (
                                                    <><Spinner className="animate-spin mr-2" size={16} />Hochladen...</>
                                                ) : (
                                                    <><Plus size={16} className="mr-2" />Bilder hinzufügen</>
                                                )}
                                            </Button>
                                        </div>
                                    </div>

                                    {images.length > 0 ? (
                                        <div className="grid grid-cols-4 gap-3">
                                            {images.map((img, idx) => (
                                                <div key={idx} className="relative group aspect-video bg-muted rounded-sm overflow-hidden">
                                                    <img
                                                        src={getImageUrl(img)}
                                                        alt={`Bild ${idx + 1}`}
                                                        className="w-full h-full object-cover"
                                                        onError={(e) => { e.target.src = 'https://images.unsplash.com/photo-1559329146-807aff9ff1fb?w=200'; }}
                                                    />
                                                    <button
                                                        type="button"
                                                        onClick={() => handleDeleteImage(idx)}
                                                        className="absolute top-1 right-1 bg-red-500 text-white p-1 rounded-sm opacity-0 group-hover:opacity-100 transition-opacity"
                                                    >
                                                        <X size={14} />
                                                    </button>
                                                    <div className="absolute bottom-1 left-1 bg-black/50 text-white text-xs px-1 rounded">
                                                        {idx + 1}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <div className="border-2 border-dashed rounded-sm p-8 text-center text-muted-foreground">
                                            <ImageIcon size={48} className="mx-auto mb-2 opacity-50" />
                                            <p>Keine Bilder vorhanden</p>
                                            <p className="text-sm">Klicken Sie auf "Bilder hinzufügen" um Bilder hochzuladen</p>
                                        </div>
                                    )}
                                </>
                            ) : (
                                <div className="border-2 border-dashed rounded-sm p-8 text-center text-muted-foreground">
                                    <Upload size={48} className="mx-auto mb-2 opacity-50" />
                                    <p>Bilder können nach dem Erstellen der Immobilie hochgeladen werden</p>
                                </div>
                            )}
                        </TabsContent>
                    </Tabs>

                    <DialogFooter className="mt-6 pt-4 border-t">
                        <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="rounded-sm">
                            Abbrechen
                        </Button>
                        <Button type="submit" disabled={submitting} className="rounded-sm" data-testid="edit-submit-btn">
                            {submitting ? (
                                <><Spinner className="animate-spin mr-2" size={16} />Speichern...</>
                            ) : (
                                'Speichern'
                            )}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}
